'use client'

import { useMemo } from 'react'
import { Box, Flex, Text, HStack, VStack } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import CryptoIcon from '../ui/CryptoIcon'
import { ClearinghouseState } from '@/lib/utils/hyper'
import { useRouter } from 'next/navigation'
import { useMarketTicker } from '@/lib/hooks/usePerpMarket'
import { formatCurrency, formatPercentageDisplay, formatPnLDisplay, formatPriceDisplay } from '@/lib/utils/format'
import TouchableOpacity from '../ui/TouchableOpacity'

interface PositionCardProps {
  position: ClearinghouseState['assetPositions'][0]
  userAddress?: string
}

export function PositionCard({ position, userAddress }: PositionCardProps) {
  const t = useTranslations()
  const router = useRouter()
  
  // Calculate position data with useMemo for performance optimization
  const  { 
    isLong, 
    isProfitable, 
    entryPrice, 
    pnl, 
    roe, 
    leverage, 
    liquidationPrice, 
    positionValue, 
    coin 
  }  = useMemo(() => {
    return {
      isLong: Number.parseFloat(position.position.szi) > 0,
      isProfitable: Number.parseFloat(position.position.unrealizedPnl) > 0,
      entryPrice: Number.parseFloat(position.position.entryPx),
      pnl: Number.parseFloat(position.position.unrealizedPnl),
      roe: Number.parseFloat(position.position.returnOnEquity),
      leverage: position.position.leverage.value,
      liquidationPrice: Number.parseFloat(position.position.liquidationPx),
      positionValue: Number.parseFloat(position.position.positionValue),
      coin: position.position.coin.replace('-PERP', '')
    }
  }, [position.position])

  const { data: marketTicker } = useMarketTicker(coin)

  const marketPrice = marketTicker?.last_price ?? marketTicker?.mark_price ?? +position.position.entryPx
  
  const handlePress = async () => {
    if (userAddress) {
      // Set navigation marker in sessionStorage to indicate navigation from trader page
      // This helps the position detail page determine if it can safely use router.back()
      const suffix = new Date().valueOf()
      const navigationKey = `nav_from_trader_${userAddress}`
      sessionStorage.setItem(navigationKey, `${suffix}`)

      router.push(`/trader/${userAddress}/position/${coin}?from=trader&nonce=${suffix}`)
    }
  }

  return (
    <>
      <TouchableOpacity
        rounded="2xl"
        bg="white/5"
        display="flex"
        flexDirection="column"
        gap={4}
        p={4}
        border="1px solid"
        borderColor="white/8"
        onClick={handlePress}
      >
        {/* Header with Token and Badges */}
        <HStack w="full" align="center" justify="space-between" className="flex-row items-center justify-between">
          <HStack align="center" gap={2.5}>
            {/* Token Icon */}
            <CryptoIcon symbol={coin} size={36}   />
            {/* Token Symbol and Badges */}
            <HStack align="center" gap={2}>
              <Text fontSize="md" color="text.t1" fontWeight="bold">{coin}</Text>
              <HStack align="center" gap={1}>
                {/* Long/Short Badge */}
                <Box
                  rounded="md"
                  bg={isLong ? 'profit/15' : 'loss/15'} 
                  px={1.5}
                  py={0.5}
                >
                  <Text
                    fontSize="xs" 
                    color={isLong ? 'profit' : 'loss'} 
                    fontWeight="medium"
                  >
                    {isLong ? t('common.long') : t('common.short')}
                  </Text>
                </Box>
                {/* Leverage Badge */}
                <Box rounded="md" bg="white/10" px={1.5} py={0.5}>
                  <Text 
                    fontSize="xs" 
                    color="text.t1/80"
                    fontWeight="medium"
                  >{leverage}x</Text>
                </Box>
              </HStack>
            </HStack>
          </HStack>
        </HStack>
        {/* Position Details */}
        <VStack w="full" gap={4}>
          {/* Top Row */}
          <HStack w="full" align="flex-end" justify="space-between">
            <VStack align="flex-start" gap={2}>
              <Text 
                fontSize="xs" 
                color="text.t1/60"
                fontWeight="medium"
              >{t('trader.position.entryPrice')}</Text>
              <Text 
                fontSize="md" 
                color="text.t1/80"
                fontWeight="medium"
              >{formatPriceDisplay(entryPrice)}</Text>
            </VStack>
            <VStack align="flex-start"  gap={2}>
              <Text 
                fontSize="xs" 
                color="text.t1/60"
                fontWeight="medium"
              >{t('trader.position.pnl')}</Text>
              <HStack align="flex-end" gap={2}>
                <Text 
                  fontSize="lg" 
                  color={isProfitable ? 'profit' : 'loss'}
                  fontWeight="medium"
                >
                  {formatPnLDisplay(pnl)}
                </Text>
                <Text 
                  fontSize="xs" 
                  color={isProfitable ? 'profit' : 'loss'}
                  fontWeight="medium"
                >
                  {formatPercentageDisplay(roe * 100)}
                </Text>
              </HStack>
            </VStack>
          </HStack>
          {/* Divider */}
          <Box w="full" h="1px" bg="white/10" />
          {/* Bottom Row */}
          <HStack w="full" align="flex-end" justify="space-between">
            <VStack align="flex-start"  gap={0.5}>
              <Text 
                fontSize="xs" 
                color="text.t1/60"
                fontWeight="medium"
              >{t('trader.position.amount')}</Text>
              <Text 
                fontSize="xs" 
                color="text.t1/80"
                fontWeight="medium"
              >
                {formatCurrency(Math.abs(positionValue))}
              </Text>
            </VStack>
            <VStack align="flex-end" gap={0.5}>
              <Text 
                fontSize="xs" 
                color="text.t1/60"
                fontWeight="medium"
              >{t('trader.position.mktLiqPrice')}</Text>
              <HStack>
                <Text 
                  fontSize="xs" 
                  color="text.t1/80"
                  fontWeight="medium"
                >
                  {formatPriceDisplay(marketPrice)}
                  {' '}
                  /
                  {' '}
                </Text>
                <Text
                  fontSize="xs" 
                  fontWeight="medium"
                  color="#ff8d59"
                >
                  {liquidationPrice ? formatPriceDisplay(liquidationPrice) : '- -'}
                </Text>
              </HStack>
            </VStack>
          </HStack>
        </VStack>
      </TouchableOpacity>
    </>
    
  )
}